import { BreakoutButton } from './BreakoutButton'
import { Dialog, InlineDialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import type { FieldError } from 'react-hook-form'
import type { Labels } from 'react-day-picker'
import {
  EMPTY_TIME_DRAFT,
  type Period,
  type TimeDraft,
  getCalendarNavRange,
  nativeTimeInputUsesTwelveHourClock,
  nextSelectableTime,
  resolveTimeDraft,
  timeDraftFromValue,
} from './BreakoutDateTimeInput.helpers'
import { FormError } from './form/FormError'
import type { FormFieldDefaults } from './types'
import { CalendarIcon } from 'components/icons/Calendar'
import { Button } from 'components/ui/button'
import { Calendar } from 'components/ui/calendar'
import { Field, FieldGroup } from 'components/ui/field'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from 'components/ui/popover'
import { ChevronDownIcon } from 'lucide-react'
import { FormLabel } from './form/FormLabel'
import classNames from 'classnames'

/**
 * '1day' hides the time half. '1min' and '15min' are equivalent: the picker
 * offers every minute; quarter-hour rounding is opted into with
 * `roundUpToQuarterHour`.
 */
type DateTimeInterval = '1min' | '15min' | '1day'

// eslint-disable-next-line react-refresh/only-export-components
export const FixedTimeComponents = {
  midnight: { hour: 0, minute: 0 },
  endOfDay: { hour: 23, minute: 59 },
}

/** The clock the native time input renders with, not the locale's default. */
function formatTime(value: DateTime, twelveHour: boolean): string {
  return value.toLocaleString({
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: twelveHour ? 'h12' : 'h23',
  })
}

type ResolutionProp =
  | {
      interval: Extract<DateTimeInterval, '1day'>
      fixedTimeComponent: { hour: number; minute: number }
    }
  | Exclude<DateTimeInterval, '1day'>

type BreakoutDateTimeProps = Omit<
  FormFieldDefaults<'input'>,
  'value' | 'onChange' | 'max' | 'min' | 'defaultValue'
> & {
  kind?: 'secondary' | 'tertiary'
  LeadingIcon?: React.ComponentType<
    { size: number } & React.SVGProps<SVGSVGElement>
  >
  iconClassName?: string
  inputClassName?: string
  min?: DateTime
  max?: DateTime
  emptyText?: string
  value?: DateTime | null
  initialValue?: DateTime | null
  defaultValue?: DateTime
  hideNowButton?: boolean
  /** "Select Now" and a day picked with no time yet round up to the next quarter hour. */
  roundUpToQuarterHour?: boolean
  initialView?: DateTime
  onChange?: (value: DateTime | null) => void
  resolution: ResolutionProp
}

export const BreakoutDateTimeInput = forwardRef<
  HTMLInputElement,
  BreakoutDateTimeProps
>(function BreakoutDateTimeInput(props, fwdRef) {
  const {
    value: _value,
    defaultValue,
    onChange,
    min,
    max,
    error,
    errorClass,
    disabled,
    label,
    name,
    required,
    resolution,
    kind = 'tertiary',
    hideNowButton,
    roundUpToQuarterHour = false,
  } = props

  const id = useId()
  const { tt } = useTranslationTyped()
  const resolutionInterval =
    typeof resolution === 'string' ? resolution : resolution.interval
  const isDateOnly = resolutionInterval === '1day'

  const [innerValue, setInnerValue] = useState<DateTime | null | undefined>(
    defaultValue ? defaultValue : null
  )

  useEffect(() => {
    if (_value === undefined) return
    setInnerValue(_value)
  }, [_value])

  const value = _value !== undefined ? _value : innerValue

  const [open, setOpen] = useState(false)
  const [timeOpen, setTimeOpen] = useState(false)
  // Column choices made while the field is still empty. Nothing is committed
  // until the draft resolves to a time (hour, plus AM/PM on a 12-hour clock),
  // so a single click can't save a silently defaulted time.
  const [timeDraft, setTimeDraft] = useState<TimeDraft>(EMPTY_TIME_DRAFT)
  // The columns match whatever clock the native time input renders with.
  const [twelveHour] = useState(() => nativeTimeInputUsesTwelveHourClock())

  useEffect(() => {
    if (!timeOpen) setTimeDraft(EMPTY_TIME_DRAFT)
  }, [timeOpen])
  const innerRef = useRef<HTMLInputElement | null>(null)
  const dateTriggerRef = useRef<HTMLButtonElement | null>(null)
  // Radix points aria-controls at the popover id even while it is unmounted;
  // reference it only while open so the attribute never dangles.
  const dateContentId = `date-picker-content-${id}`
  const timeContentId = `time-picker-content-${id}`

  const minDate = min ? min.startOf('day').toJSDate() : undefined
  const maxDate = max ? max.endOf('day').toJSDate() : undefined

  const dateLabel = label ?? tt.design_system.date()

  const { startMonth, endMonth } = getCalendarNavRange({
    min,
    max,
    value,
    now: DateTime.now(),
  })

  // Same shell as BreakoutTextInput / BreakoutSelect so the two halves sit
  // flush with the other fields in a form. Not run through twMerge, which
  // drops the bare `outline` class and with it the border.
  const errorId = `${id}-error`
  const timeDescriptionId = `${id}-time-description`
  const describedBy = (...ids: (string | false | undefined)[]) =>
    ids.filter(Boolean).join(' ') || undefined
  const shellClass = classNames(
    'flex h-[3.25rem] w-full items-center rounded-2xl text-label-medium outline outline-1 outline-outline-light',
    {
      'outline-core-error': !!error,
      'bg-core-tertiary': kind === 'tertiary' && !disabled,
      'bg-core-secondary': kind === 'secondary' && !disabled,
      'bg-surface-dim': kind === 'secondary' && disabled,
      'bg-surface': kind === 'tertiary' && disabled,
      'text-on-surface': !disabled,
      'text-on-surface-disabled cursor-not-allowed': disabled,
    }
  )

  useEffect(() => {
    if (!open && !timeOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        e.preventDefault()
        if (timeOpen) setTimeOpen(false)
        else setOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown, { capture: true })
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true })
    }
  }, [open, timeOpen])

  const updateValue = useCallback(
    (newValue: DateTime | null) => {
      let adjusted = newValue
      if (
        adjusted &&
        typeof resolution === 'object' &&
        resolution.interval === '1day'
      ) {
        adjusted = adjusted.set({
          hour: resolution.fixedTimeComponent.hour,
          minute: resolution.fixedTimeComponent.minute,
          second: 0,
          millisecond: 0,
        })
      }
      setInnerValue(adjusted)
      onChange?.(adjusted)
    },
    [onChange, resolution]
  )

  const commitTime = useCallback(
    (time: { hour: number; minute: number }) => {
      const current = value ?? DateTime.now()
      updateValue(
        current.set({
          hour: time.hour,
          minute: time.minute,
          second: 0,
          millisecond: 0,
        })
      )
    },
    [updateValue, value]
  )

  // An existing value is always a complete draft, so every pick applies at
  // once; an empty field accumulates picks until the draft resolves.
  const selected = value ? timeDraftFromValue(value, twelveHour) : timeDraft
  const pickTime = (patch: Partial<TimeDraft>) => {
    const next = { ...selected, ...patch }
    const resolved = resolveTimeDraft(next, twelveHour)
    if (resolved) {
      commitTime(resolved)
      setTimeDraft(EMPTY_TIME_DRAFT)
    } else {
      setTimeDraft(next)
    }
  }
  const hourOptions = twelveHour
    ? Array.from({ length: 12 }, (_, i) => i + 1)
    : Array.from({ length: 24 }, (_, i) => i)
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i)
  const periodOptions: Period[] = ['AM', 'PM']
  const monthName = (month: Date | undefined) =>
    month ? DateTime.fromJSDate(month).toFormat('LLLL yyyy') : ''
  const calendarLabels: Partial<Labels> = {
    labelPrevious: (month) =>
      tt.inputs.previous_month({ month: monthName(month) }),
    labelNext: (month) => tt.inputs.next_month({ month: monthName(month) }),
    labelMonthDropdown: () => tt.design_system.choose_month(),
    labelYearDropdown: () => tt.design_system.choose_year(),
    labelDayButton: (date, modifiers) => {
      const day = DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_HUGE)
      return modifiers.today ? `${day}, ${tt.design_system.today()}` : day
    },
  }
  // What the columns have so far and what is still needed, e.g.
  // "Hour 3, Minute 45 chosen, choose AM or PM".
  const draftAnnouncement = () => {
    const chosen = [
      timeDraft.hour !== null &&
        tt.inputs.time_draft_part_hour({
          hour: twelveHour
            ? String(timeDraft.hour)
            : String(timeDraft.hour).padStart(2, '0'),
        }),
      timeDraft.minute !== null &&
        tt.inputs.time_draft_part_minute({
          minute: String(timeDraft.minute).padStart(2, '0'),
        }),
      timeDraft.period !== null && periodLabel(timeDraft.period),
    ].filter((part): part is string => !!part)
    if (chosen.length === 0) return ''
    return tt.inputs.time_draft_progress({
      chosen: chosen.join(', '),
      missing:
        timeDraft.hour === null
          ? tt.inputs.time_draft_missing_hour()
          : tt.inputs.time_draft_missing_period(),
    })
  }
  const periodLabel = (period: Period) =>
    (period === 'AM'
      ? tt.design_system.am()
      : tt.design_system.pm()
    ).toUpperCase()

  return (
    <div className="flex flex-col items-start">
      {/* Hidden native input for form compatibility */}
      <div className="relative h-0 w-0 overflow-hidden">
        <input
          ref={(e) => {
            innerRef.current = e
            if (!fwdRef) return
            if (typeof fwdRef === 'function') {
              fwdRef(e)
            } else {
              fwdRef.current = e
            }
          }}
          required={required}
          name={name}
          id={id}
          disabled={disabled}
          data-testid={`datetime-input-${name}`}
          type="datetime-local"
          className="absolute hidden opacity-0"
          // A required input that is display:none blocks native form submit
          // silently, since the browser cannot focus it to show the message.
          // Send focus to the visible trigger so the block is perceivable.
          onInvalid={(e) => {
            e.preventDefault()
            dateTriggerRef.current?.focus()
          }}
          onChange={(e) => {
            const datetime = e.target.value
              ? DateTime.fromISO(e.target.value)
              : null
            updateValue(datetime)
          }}
          value={
            value
              ? value.toFormat('yyyy-LL-dd') + 'T' + value.toFormat('HH:mm')
              : ''
          }
        />
      </div>

      <FieldGroup className="flex-row gap-3">
        <Field className="gap-1">
          <FormLabel
            for={`date-picker-${id}`}
            required={required}
            label={dateLabel}
          />
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                ref={dateTriggerRef}
                type="button"
                autoFocus={props.autoFocus}
                id={`date-picker-${id}`}
                aria-controls={open ? dateContentId : undefined}
                disabled={disabled}
                data-testid={`datetime-field-${name}`}
                aria-label={
                  value
                    ? tt.design_system.date_picker_aria_label({
                        label: dateLabel,
                        date: value.toLocaleString(DateTime.DATE_FULL),
                      })
                    : tt.design_system.no_date_selected({ label: dateLabel })
                }
                // aria-required / aria-invalid are not permitted on a button
                // (ARIA 1.2); the error text is linked instead.
                aria-describedby={describedBy(!!error && errorId)}
                className={classNames(
                  shellClass,
                  'date-field justify-between gap-2 px-4 py-1',
                  { 'focus-ring-edge': !disabled }
                )}
              >
                <span
                  className={classNames('inline-flex items-center gap-1', {
                    'text-on-surface-var': !value,
                  })}
                >
                  <CalendarIcon
                    aria-hidden="true"
                    size={10}
                    className="mb-[1px] !size-auto"
                  />
                  {value
                    ? value.toLocaleString(DateTime.DATE_FULL)
                    : tt.design_system.select_date()}
                </span>
                <ChevronDownIcon
                  aria-hidden="true"
                  className="size-4 shrink-0"
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              // Cap the popover to the space Radix has between the trigger and
              // the (iframe) viewport edge and scroll internally, so the
              // calendar and its controls stay reachable inside a short LMS
              // iframe instead of overflowing off-screen. See BL-4699.
              id={dateContentId}
              className="flex max-h-[var(--radix-popover-content-available-height)] w-auto flex-col overflow-y-auto p-0"
              align="start"
              collisionPadding={8}
              aria-label={tt.design_system.choose_date()}
              // Let react-day-picker place focus on the selected day (or
              // today) instead of Radix landing on the previous-month button.
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <Calendar
                mode="single"
                autoFocus
                labels={calendarLabels}
                footer={
                  value
                    ? tt.inputs.selected_date({
                        date: value.toLocaleString(DateTime.DATE_FULL),
                      })
                    : undefined
                }
                selected={value?.toJSDate()}
                captionLayout="dropdown"
                startMonth={startMonth}
                endMonth={endMonth}
                defaultMonth={value?.toJSDate()}
                onSelect={(date) => {
                  if (!date) return
                  const luxonDate = DateTime.fromJSDate(date)
                  const current =
                    value ??
                    nextSelectableTime(DateTime.now(), roundUpToQuarterHour)
                  const merged = current.set({
                    year: luxonDate.year,
                    month: luxonDate.month,
                    day: luxonDate.day,
                  })
                  updateValue(merged)
                  setOpen(false)
                }}
                disabled={[
                  ...(minDate ? [{ before: minDate }] : []),
                  ...(maxDate ? [{ after: maxDate }] : []),
                ]}
              />
              {!hideNowButton && (
                <div className="border-t px-3 pb-2 pt-1">
                  <Button
                    variant="ghost"
                    data-testid="datetime-picker-now"
                    className="h-8 w-full !text-label-medium"
                    onClick={() => {
                      updateValue(
                        nextSelectableTime(DateTime.now(), roundUpToQuarterHour)
                      )
                      setOpen(false)
                    }}
                  >
                    {isDateOnly
                      ? tt.design_system.select_today()
                      : tt.design_system.select_now()}
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </Field>
        {!isDateOnly && (
          <Field className="w-36 gap-1">
            <FormLabel
              for={`time-picker-${id}`}
              required={required}
              label={tt.design_system.time()}
            />
            <Popover open={timeOpen} onOpenChange={setTimeOpen}>
              {/* Anchor to the whole field, not the caret, so the popover sits
                  under the field edge exactly like the date popover does. */}
              <PopoverAnchor asChild>
                <div
                  // The shell draws the native input's focus ring (the input
                  // has outline-none). Scope it to the input so focus on the
                  // caret shows only the caret's own ring, like the date trigger.
                  className={classNames(
                    shellClass,
                    'time-field gap-1 pl-4 pr-2',
                    {
                      // Ring only when the input itself has focus, so the
                      // caret's own ring never doubles up. Browsers without
                      // :has() (Firefox < 121, Safari < 15.4) fall back to
                      // focus-within.
                      'has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-focus-ring [@supports_not_selector(:has(*))]:focus-within:outline-2 [@supports_not_selector(:has(*))]:focus-within:outline-focus-ring':
                        !disabled,
                    }
                  )}
                >
                  {/* Native so the hour, minute and AM/PM segments edit in
                      place with the arrow keys; the browser's own clock button
                      is hidden because the caret opens our picker. */}
                  <input
                    type="time"
                    step={60}
                    id={`time-picker-${id}`}
                    disabled={disabled}
                    data-testid={`datetime-time-${name}`}
                    aria-required={required}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy(
                      timeDescriptionId,
                      !!error && errorId
                    )}
                    value={value ? value.toFormat('HH:mm') : ''}
                    onChange={(e) => {
                      if (!e.target.value) return
                      const [hour, minute] = e.target.value
                        .split(':')
                        .map(Number)
                      commitTime({ hour, minute })
                    }}
                    className={classNames(
                      'min-w-0 flex-1 appearance-none bg-transparent text-label-medium outline-none disabled:cursor-not-allowed [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none',
                      { 'text-on-surface-var': !value }
                    )}
                  />
                  {/* Inside the shell, not a direct child of Field: Field sets
                      `[&>.sr-only]:w-auto`, which would undo sr-only's 1px width
                      and leave an invisible box wide enough to scroll a dialog. */}
                  <div id={timeDescriptionId} className="sr-only">
                    {tt.design_system.time_input_description()}
                  </div>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={disabled}
                      data-testid={`datetime-time-trigger-${name}`}
                      aria-label={tt.design_system.edit_time()}
                      aria-controls={timeOpen ? timeContentId : undefined}
                      className="focus-ring-edge h-8 w-8 shrink-0"
                    >
                      <ChevronDownIcon aria-hidden="true" />
                    </Button>
                  </PopoverTrigger>
                </div>
              </PopoverAnchor>
              <PopoverContent
                // Same height cap as the date popover so the columns scroll
                // inside a short LMS iframe instead of being clipped. See BL-4699.
                id={timeContentId}
                className="time-picker flex max-h-[var(--radix-popover-content-available-height)] w-auto flex-col overflow-hidden p-0"
                align="end"
                collisionPadding={8}
                aria-label={tt.inputs.select_time()}
              >
                <div className="sr-only" aria-live="polite">
                  {value
                    ? tt.inputs.selected_time({
                        time: formatTime(value, twelveHour),
                      })
                    : draftAnnouncement()}
                </div>
                <div className="time-picker-columns flex min-h-0 gap-1 p-2">
                  <TimeColumn
                    label={tt.design_system.hour()}
                    testId="datetime-time-hours"
                    options={hourOptions.map((h) => ({
                      value: h,
                      label: twelveHour
                        ? String(h)
                        : String(h).padStart(2, '0'),
                    }))}
                    selected={selected.hour}
                    onSelect={(h) => pickTime({ hour: h })}
                  />
                  <TimeColumn
                    label={tt.design_system.minute()}
                    testId="datetime-time-minutes"
                    options={minuteOptions.map((m) => ({
                      value: m,
                      label: String(m).padStart(2, '0'),
                    }))}
                    selected={selected.minute}
                    onSelect={(m) => pickTime({ minute: m })}
                  />
                  {twelveHour && (
                    <TimeColumn
                      label={tt.design_system.am_pm()}
                      testId="datetime-time-period"
                      options={periodOptions.map((p) => ({
                        value: p,
                        label: periodLabel(p),
                      }))}
                      selected={selected.period}
                      onSelect={(p) => pickTime({ period: p })}
                    />
                  )}
                </div>
                <div className="time-picker-footer flex items-center justify-between gap-3 border-t border-outline-light px-3 py-2">
                  <div className="text-body-small text-on-surface-var">
                    {tt.design_system.timezone({
                      timezone: DateTime.local().toFormat('ZZZZ'),
                    })}
                  </div>
                  <Button
                    variant="ghost"
                    data-testid="datetime-time-done"
                    className="h-8 !text-label-medium"
                    onClick={() => setTimeOpen(false)}
                  >
                    {tt.common.done()}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </Field>
        )}
      </FieldGroup>
      <FormError error={error} errorClass={errorClass} id={errorId} />
    </div>
  )
})

/**
 * One scrollable listbox of the time picker. Only the selected option (or
 * the first one) is in the tab order; arrow keys move within the column.
 */
function TimeColumn<T extends string | number>({
  label,
  testId,
  options,
  selected,
  onSelect,
}: {
  label: string
  testId: string
  options: { value: T; label: string }[]
  selected: T | null
  onSelect: (value: T) => void
}) {
  const listRef = useRef<HTMLDivElement | null>(null)
  const labelId = useId()

  useEffect(() => {
    const list = listRef.current
    const el = list?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!list || !el) return
    list.scrollTop = el.offsetTop - list.clientHeight / 2 + el.clientHeight / 2
  }, [selected])

  const moveFocus = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const buttons = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []
    )
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement)
    if (index === -1) return
    const next =
      e.key === 'ArrowDown'
        ? Math.min(index + 1, buttons.length - 1)
        : e.key === 'ArrowUp'
          ? Math.max(index - 1, 0)
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? buttons.length - 1
              : null
    if (next === null) return
    e.preventDefault()
    buttons[next]?.focus()
  }

  const hasSelection = options.some((o) => o.value === selected)

  return (
    <div className="time-picker-column flex min-h-0 w-16 flex-col">
      <div
        id={labelId}
        className="py-2 text-center text-label-small uppercase text-on-surface-var"
      >
        {label}
      </div>
      <div
        ref={listRef}
        role="listbox"
        aria-labelledby={labelId}
        data-testid={testId}
        onKeyDown={moveFocus}
        className="flex max-h-52 min-h-0 flex-col gap-0.5 overflow-y-auto"
      >
        {options.map((option, index) => {
          const isSelected = option.value === selected
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={isSelected}
              tabIndex={isSelected || (!hasSelection && index === 0) ? 0 : -1}
              onClick={() => onSelect(option.value)}
              // Inset ring like BreakoutSelect's options: the column scrolls,
              // so an outline drawn outside the button would be clipped.
              className="focus-ring-inside h-8 w-full shrink-0 rounded-lg text-label-medium text-on-surface hover:bg-surface focus-visible:ring-2 focus-visible:ring-inset aria-selected:bg-core-primary aria-selected:text-core-on-primary forced-colors:aria-selected:outline forced-colors:aria-selected:outline-2"
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function DatePickerDialog({
  value,
  show,
  onChange,
  onCancel,
  min,
  max,
  header,
  hideNowButton,
  resolution = '15min',
  transitionDuration,
  useInlineDialog = true,
}: {
  value: DateTime | null
  content: string
  show: boolean
  hideNowButton?: boolean
  error: FieldError | undefined
  onChange: (dateTime: DateTime | null) => void
  onCancel: () => void
  header?: string | React.ReactNode
  min?: DateTime
  max?: DateTime
  initialView?: DateTime
  initialValue?: DateTime
  transitionDuration?: number
  useInlineDialog?: boolean
  resolution?: ResolutionProp
}) {
  const DialogToUse = useInlineDialog ? InlineDialog : Dialog
  const { tt } = useTranslationTyped()
  const resolutionInterval =
    typeof resolution === 'string' ? resolution : resolution.interval
  const isDateOnly = resolutionInterval === '1day'
  const defaultHeader = isDateOnly
    ? tt.design_system.choose_date()
    : tt.design_system.choose_date_and_time()
  const resolvedHeader = header ?? defaultHeader
  const buttonRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [selectedValue, setSelectedValue] = useState<DateTime | null>(value)

  return (
    <DialogToUse
      open={show}
      onDismiss={() => onCancel()}
      size="custom"
      className="relative min-w-[400px] max-w-[400px] rounded-3xl bg-surface-bright p-13"
      transitionDuration={transitionDuration}
      dismissOnEscapeKey
      testId="datetime-dialog"
      aria-labelledby={undefined}
      aria-label={tt.inputs.date_time_picker()}
    >
      <DialogCloseButton
        buttonRef={buttonRef}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onCancel()
          return false
        }}
      />
      <div className="mb-3 text-left text-headline-medium">
        {resolvedHeader}
      </div>
      <div className="py-4" ref={contentRef}>
        <BreakoutDateTimeInput
          name="datepicker"
          autoFocus
          value={selectedValue}
          onChange={setSelectedValue}
          min={min}
          max={max}
          hideNowButton={hideNowButton}
          resolution={resolution}
        />
      </div>
      <BreakoutButton
        data-testid="datetime-picker-ok"
        kind="primary"
        size="large"
        fullWidth
        className="mt-2"
        disabled={!selectedValue}
        onClick={() => {
          if (selectedValue) onChange(selectedValue)
        }}
      >
        {tt.design_system.choose_date()}
      </BreakoutButton>
    </DialogToUse>
  )
}
